import { Pencil } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { ParcelCard, RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { useTranslate } from 'shared/lib/i18n'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { Textarea } from 'shared/components/ui/textarea'
import { Hint } from 'shared/components/section'
import { focusFirstField } from './dialog-focus'

export type ParcelEditRequest = RouterInputs['parcelEdits']['Edit']
export type ParcelEditAnswer = RouterOutputs['parcelEdits']['Edit']
export type EditPoint = RouterOutputs['buyerReturns']['Points'][number]
type Detail = ParcelCard
export type EditVerdicts = NonNullable<Detail['edits']>

const editViaTrpc = (input: ParcelEditRequest) => trpcClient.parcelEdits.Edit.mutate(input)
const pointsViaTrpc = () => trpcClient.buyerReturns.Points.query()

/**
 * «Править» on the parcel card — support's edit (ADR-0038, spec §19, user story 119;
 * ticket 44): the Point (one of the Points on TezGo), the Recipient (name, client
 * code; the document for a partner identifying by document — support types it in
 * full), the amount owed — with a comment, always. What may not be edited at this
 * stage is closed with the server's reason (`edits`: the amount of a paid Parcel is
 * "paid, the amount is fixed"; a buyer's return only its Recipient; a Parcel whose
 * way has ended nothing). Only what changed is sent; a refusal is said in words.
 *
 * «Править» sits in the card's header; the form opens in a dialog.
 *
 * The calls are injected (`edit`, `listPoints`) so the panel is driven at its props in
 * `bun test`.
 */
export function ParcelEditPanel({
	parcel,
	edits,
	edit = editViaTrpc,
	listPoints = pointsViaTrpc,
	onEdited,
}: {
	parcel: Pick<Detail, 'parcelId' | 'destinationPointId' | 'recipient' | 'amountOwed' | 'recipientDocument'>
	edits: EditVerdicts
	edit?: (input: ParcelEditRequest) => Promise<ParcelEditAnswer>
	listPoints?: () => Promise<EditPoint[]>
	onEdited: () => void
}) {
	const { t } = useTranslate()
	const [open, setOpen] = useState(false)
	const [points, setPoints] = useState<EditPoint[]>([])
	const initial = () => ({
		pointId: parcel.destinationPointId,
		name: parcel.recipient.name,
		clientRef: parcel.recipient.clientRef,
		document: parcel.recipientDocument?.value ?? '',
		amount: String(parcel.amountOwed),
		comment: '',
	})
	const [form, setForm] = useState(initial)
	const [busy, setBusy] = useState(false)
	const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
	// Saved: the dialog says so and offers only «Закрыть».
	const [saved, setSaved] = useState(false)

	useEffect(() => {
		if (!open || !edits.point.allowed) return
		listPoints().then(setPoints, () => setPoints([]))
	}, [open, edits.point.allowed, listPoints])

	const reasonText = (reason: string | null) => (reason ? t(`parcelEdit.closed_${reason}` as never) : '')

	const submit = async () => {
		const comment = form.comment.trim()
		if (!comment) {
			setMessage({ ok: false, text: t('parcelEdit.commentRequired') })
			return
		}
		const request: ParcelEditRequest = { parcelId: parcel.parcelId, comment }
		if (edits.point.allowed && form.pointId && form.pointId !== parcel.destinationPointId) {
			request.pointId = form.pointId
		}
		const documentChanged =
			edits.documentType !== null && form.document.trim() !== '' && form.document.trim() !== (parcel.recipientDocument?.value ?? '')
		if (
			edits.recipient.allowed &&
			(form.name.trim() !== parcel.recipient.name || form.clientRef.trim() !== parcel.recipient.clientRef || documentChanged)
		) {
			request.recipient = {
				name: form.name.trim(),
				clientRef: form.clientRef.trim(),
				...(documentChanged ? { document: form.document.trim() } : {}),
			}
		}
		const amount = Number(form.amount)
		if (edits.amount.allowed && form.amount.trim() !== '' && amount !== parcel.amountOwed) {
			if (!Number.isInteger(amount) || amount < 0) {
				setMessage({ ok: false, text: t('parcelEdit.amountInvalid') })
				return
			}
			request.amountOwed = amount
		}
		if (!request.pointId && !request.recipient && request.amountOwed === undefined) {
			setMessage({ ok: false, text: t('parcelEdit.nothingChanged') })
			return
		}
		setBusy(true)
		setMessage(null)
		try {
			const answer = await edit(request)
			if (answer.outcome === 'refused') {
				setMessage({ ok: false, text: t(`parcelEdit.refused_${answer.reason}` as never) })
				return
			}
			setMessage({
				ok: true,
				text: answer.outcome === 'edited' ? t('parcelEdit.done', { point: answer.point.id }) : t('parcelEdit.unchanged'),
			})
			setForm((f) => ({ ...f, comment: '' }))
			setSaved(true)
			onEdited()
		} catch (err) {
			setMessage({ ok: false, text: err instanceof Error ? err.message : String(err) })
		} finally {
			setBusy(false)
		}
	}

	const closed = (reason: string | null, testId: string) => (
		<p className='text-xs text-muted-foreground' data-testid={testId}>
			{reasonText(reason)}
		</p>
	)

	const close = () => {
		setOpen(false)
		setForm(initial())
		setMessage(null)
		setSaved(false)
	}

	return (
		<>
			<Button variant='outline' onClick={() => setOpen(true)} data-testid='parcel-edit-open'>
				<Pencil />
				{t('parcelEdit.open')}
			</Button>
			<Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : !busy && close())}>
				<DialogContent className='sm:max-w-xl' data-testid='parcel-edit' onOpenAutoFocus={focusFirstField}>
					<DialogHeader>
						<DialogTitle className='flex items-center gap-2'>
							{t('parcelEdit.title')}
							<Hint>{t('parcelEdit.hint')}</Hint>
						</DialogTitle>
						<DialogDescription className='sr-only'>{t('parcelEdit.hint')}</DialogDescription>
					</DialogHeader>
					{!saved && (
						<div className='grid gap-4'>
							<div className='grid gap-1.5'>
								<Label htmlFor='parcel-edit-point'>{t('parcelEdit.point')}</Label>
								{edits.point.allowed ? (
									<select
										id='parcel-edit-point'
										data-testid='parcel-edit-point'
										className='h-9 rounded-md border border-input bg-transparent px-2.5 text-sm'
										value={form.pointId}
										onChange={(e) => setForm((f) => ({ ...f, pointId: e.target.value }))}
									>
										{!points.some((p) => p.pointId === parcel.destinationPointId) && (
											<option value={parcel.destinationPointId}>{t('parcelEdit.currentPoint')}</option>
										)}
										{points.map((p) => (
											<option key={p.pointId} value={p.pointId}>
												{p.id}
											</option>
										))}
									</select>
								) : (
									closed(edits.point.reason, 'parcel-edit-point-closed')
								)}
							</div>
							<div className='grid gap-4 sm:grid-cols-2'>
								<div className='grid gap-1.5'>
									<Label htmlFor='parcel-edit-name'>{t('parcelEdit.recipientName')}</Label>
									<Input
										id='parcel-edit-name'
										data-testid='parcel-edit-name'
										disabled={!edits.recipient.allowed}
										value={form.name}
										onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
									/>
								</div>
								<div className='grid gap-1.5'>
									<Label htmlFor='parcel-edit-client'>{t('parcelEdit.clientRef')}</Label>
									<Input
										id='parcel-edit-client'
										data-testid='parcel-edit-client'
										disabled={!edits.recipient.allowed}
										value={form.clientRef}
										onChange={(e) => setForm((f) => ({ ...f, clientRef: e.target.value }))}
									/>
								</div>
								{edits.documentType && (
									<div className='grid gap-1.5'>
										<Label htmlFor='parcel-edit-document'>
											{t('parcelEdit.document', {
												type: t(`policies.documentType_${edits.documentType}` as never),
											})}
										</Label>
										<Input
											id='parcel-edit-document'
											data-testid='parcel-edit-document'
											disabled={!edits.recipient.allowed}
											value={form.document}
											onChange={(e) => setForm((f) => ({ ...f, document: e.target.value }))}
										/>
									</div>
								)}
								<div className='grid gap-1.5'>
									<Label htmlFor='parcel-edit-amount'>{t('parcelEdit.amount')}</Label>
									{edits.amount.allowed ? (
										<Input
											id='parcel-edit-amount'
											data-testid='parcel-edit-amount'
											type='number'
											min={0}
											value={form.amount}
											onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
										/>
									) : (
										closed(edits.amount.reason, 'parcel-edit-amount-closed')
									)}
								</div>
							</div>
							{!edits.recipient.allowed && closed(edits.recipient.reason, 'parcel-edit-recipient-closed')}
							<div className='grid gap-1.5'>
								<Label htmlFor='parcel-edit-comment'>{t('parcelEdit.comment')}</Label>
								<Textarea
									id='parcel-edit-comment'
									data-testid='parcel-edit-comment'
									placeholder={t('parcelEdit.commentPlaceholder')}
									value={form.comment}
									onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
								/>
							</div>
						</div>
					)}
					{message && (
						<p
							role={message.ok ? 'status' : 'alert'}
							className={message.ok ? 'text-sm' : 'text-sm text-destructive'}
							data-testid='parcel-edit-message'
						>
							{message.text}
						</p>
					)}
					<DialogFooter>
						<Button variant='outline' onClick={close} disabled={busy}>
							{t('parcelEdit.cancel')}
						</Button>
						{!saved && (
							<Button onClick={submit} disabled={busy} data-testid='parcel-edit-submit'>
								{t('parcelEdit.submit')}
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
